import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { Badge } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { TokenTheoModel } from '../../goi-nap-token/components/TokenTheoModel';
import { GOI_DICH_VU } from '../constants';
import { cacTuiTheoThuTu, conLaiCapThem, phanTramTui, tuiHetHanSomNhat } from '../nghiep-vu/cap-them-token';
import type { TuiToken } from '../nghiep-vu/cap-them-token';
import { nguonHanMuc } from '../nghiep-vu/han-muc-token';
import type { AiTokenQuota } from '../types';

/** Ai đang xem: Platform Admin nói "tenant này", Tenant Admin nói "tổ chức" (ADR-UI-0010: chữ theo người đọc). */
export type DoiTuongHanMuc = 'tenant' | 'to-chuc';

function useTenGoi(q: AiTokenQuota): string {
  const { t } = useT();
  return GOI_DICH_VU[q.plan_code] ? t(GOI_DICH_VU[q.plan_code]) : q.plan_code;
}

/**
 * Hai câu ngắn dưới thanh tiến độ (ADR-0091 mục 1, 4): hạn mức tháng và nó đến từ đâu ("Hạn mức: 50.000.000
 * token/tháng · theo gói tài nguyên Chuyên nghiệp"), rồi token cấp thêm còn lại ("+ 5.000.000 token cấp thêm còn lại
 * (đến 30/11/2026)"). Không dùng thuật ngữ của công thức: người đọc chỉ cần biết số và lý do.
 */
export function DongHanMuc({ q, doiTuong }: { q: AiTokenQuota; doiTuong: DoiTuongHanMuc }) {
  const { t, locale } = useT();
  const tenGoi = useTenGoi(q);
  const nguon = nguonHanMuc(q);
  const tenGoiTN = q.resource_package?.name ?? tenGoi;
  const conLai = conLaiCapThem(q);
  const somNhat = tuiHetHanSomNhat(q);

  const lyDo =
    nguon === 'ghi-de'
      ? t(doiTuong === 'tenant' ? 'tokenAi.hmRiengTenant' : 'tokenAi.hmRiengToChuc')
      : nguon === 'goi-tai-nguyen'
        ? t('tokenAi.hmTheoGoiTaiNguyen', { ten: tenGoiTN })
        : t('tokenAi.hmGoiChuaDat', { ten: tenGoiTN });

  return (
    <div className="space-y-0.5 text-sm text-content-secondary">
      <p className="tabular-nums">
        <span className="font-semibold text-content-primary">
          {q.base_limit === null ? t('tokenAi.hmKhongGioiHan') : t('tokenAi.hmSo', { so: soVN(q.base_limit, locale) })}
        </span>
        <span aria-hidden> · </span>
        <span>{lyDo}</span>
      </p>
      {q.grants.length > 0 && somNhat && (
        <p className="tabular-nums">
          {q.grants.length === 1
            ? t('tokenAi.hmCapThemMot', { so: soVN(conLai, locale), ngay: ngayVN(somNhat.valid_to) })
            : t('tokenAi.hmCapThemNhieu', {
                so: soVN(conLai, locale),
                lan: soVN(q.grants.length, locale),
                ngay: ngayVN(somNhat.valid_to),
              })}
        </p>
      )}
    </div>
  );
}

function DongTui({ stt, tui, q, doiTuong }: { stt: number; tui: TuiToken; q: AiTokenQuota; doiTuong: DoiTuongHanMuc }) {
  const { t, locale } = useT();
  const tenGoi = useTenGoi(q);
  const laGoi = tui.loai === 'goi';
  const tong = laGoi ? tui.tong : tui.tui.tokens;
  const daDung = laGoi ? tui.daDung : tui.tui.used;
  const conLai = laGoi ? tui.conLai : tui.tui.remaining;
  const nhan = laGoi
    ? q.limit_override !== null
      ? t(doiTuong === 'tenant' ? 'tokenAi.ctTuiRiengTenant' : 'tokenAi.ctTuiRiengToChuc')
      : t('tokenAi.ctTuiGoi', { ten: q.resource_package?.name ?? tenGoi })
    : t(tui.tui.kind === 'NAP_THEM' ? 'tokenAi.ctTuiNap' : 'tokenAi.ctTuiTamThoi');
  const phu = laGoi ? t('tokenAi.ctVeKhong', { ngay: ngayVN(tui.veKhong) }) : t('tokenAi.ctDungToi', { ngay: ngayVN(tui.tui.valid_to) });

  return (
    <li className="flex min-w-0 gap-3 py-2">
      <span
        aria-hidden
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface text-2xs font-semibold text-content-secondary border border-default"
      >
        {stt}
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-content-primary">
            {nhan}
            {!laGoi && tui.tui.expiring_soon && <Badge variant="warning">{t('tokenAi.sapHetHan')}</Badge>}
          </span>
          <span className="text-sm tabular-nums text-content-primary">
            {t('tokenAi.ctConLai', { so: soVN(conLai, locale) })}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface">
          <div className="h-full rounded-full bg-primary-400" style={{ width: `${phanTramTui(daDung, tong)}%` }} />
        </div>
        <p className="flex flex-wrap justify-between gap-x-3 text-xs tabular-nums text-content-muted">
          <span>{t('tokenAi.ctDaDungTren', { daDung: soVN(daDung, locale), tong: soVN(tong, locale) })}</span>
          <span>{phu}</span>
        </p>
      </div>
    </li>
  );
}

/**
 * "Cách tính hạn mức", gập sẵn (`<details>`: bàn phím mở bằng Enter hoặc Space, trình đọc màn hình báo trạng thái
 * mở). Liệt kê các túi token **theo thứ tự trừ** (ADR-0091 mục 4): túi gói tài nguyên của tháng (về 0 đầu tháng sau)
 * và từng túi cấp thêm còn hiệu lực (số dư riêng, dùng tới ngày hết hạn, nhãn "Sắp hết hạn" khi còn ≤ 7 ngày), rồi tổng.
 */
export function CachTinhHanMuc({ q, doiTuong }: { q: AiTokenQuota; doiTuong: DoiTuongHanMuc }) {
  const { t, locale } = useT();
  const cacTui = cacTuiTheoThuTu(q);
  // Bảng theo model chỉ tải khi người xem mở khối (ADR-0100).
  const [daMo, setDaMo] = useState(false);

  return (
    <details className="group rounded border border-default bg-subtle" onToggle={(e) => setDaMo(e.currentTarget.open)}>
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded px-3 py-2 text-sm font-medium text-content-secondary hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 [&::-webkit-details-marker]:hidden">
        <ChevronRight size={15} aria-hidden className="shrink-0 transition-transform group-open:rotate-90" />
        {t('tokenAi.ctTieuDe')}
      </summary>
      <div className="border-t border-default px-3 py-2">
        {cacTui.length > 0 && (
          <>
            <p className="text-xs text-content-muted">
              {t(q.priority === 'MUA_THEM_TRUOC' ? 'tokenAi.ctThuTuMua' : 'tokenAi.ctThuTuHet')}
            </p>
            <ol className="divide-y divide-default">
              {cacTui.map((tui, i) => (
                <DongTui key={tui.loai === 'goi' ? 'goi' : tui.tui.grant_id} stt={i + 1} tui={tui} q={q} doiTuong={doiTuong} />
              ))}
            </ol>
          </>
        )}
        <div className={cn('flex items-baseline justify-between gap-3 pt-2', cacTui.length > 0 && 'border-t border-default')}>
          <span className="text-sm font-semibold text-content-primary">{t('tokenAi.ctTongConLai')}</span>
          <span className="text-sm font-semibold tabular-nums text-content-primary">
            {q.limit_value === null || q.remaining === null
              ? t('hanMucToken.khongGioiHan')
              : t('tokenAi.ctTongGiaTri', { conLai: soVN(q.remaining, locale), tong: soVN(q.limit_value, locale) })}
          </span>
        </div>
        <div className="mt-2">
          {/* Chi tiết tenant của cổng nền tảng thêm token thật và hệ số; tổ chức chỉ thấy token Fox (ADR-0123 mục 3). */}
          <TokenTheoModel tenantId={q.tenant_id} batDau={daMo} phamVi={doiTuong === 'tenant' ? 'nen-tang' : 'tenant'} />
        </div>
      </div>
    </details>
  );
}
